// src/components/ecommerce/CustomerManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Users,
  Search,
  Filter,
  UserPlus,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ShoppingCart,
  DollarSign,
  Star,
  Eye,
  Edit,
  MessageCircle,
  Download,
  Upload,
  RefreshCw,
  MoreHorizontal,
  TrendingUp,
  Award,
  Heart,
  Gift,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  Send,
  Tag,
  Globe,
  Smartphone,
  Building,
  CreditCard,
  Package,
  Truck
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { CustomerDetailModal } from './CustomerDetailModal';

export const CustomerManager = ({ storeId, storeData }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [sortBy, setSortBy] = useState('lastOrder');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerStats, setCustomerStats] = useState({});

  useEffect(() => {
    loadCustomers();
    loadCustomerStats();
  }, [storeId]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const customersData = await ecommerceService.getCustomers(storeId);
      setCustomers(customersData);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerStats = async () => {
    try {
      const stats = await ecommerceService.getCustomerStats(storeId);
      setCustomerStats(stats);
    } catch (error) {
      console.error('Error loading customer stats:', error);
    }
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerDetail(true);
  };

  const handleContactCustomer = (customer, method) => {
    switch (method) {
      case 'email':
        window.open(`mailto:${customer.email}?subject=Hello from ${storeData.storeName}`);
        break;
      case 'phone':
        window.open(`tel:${customer.phone}`);
        break;
      case 'whatsapp':
        if (customer.phone && storeData.integrations?.whatsapp) {
          const message = `Hi ${customer.name}! Thanks for being a valued customer of ${storeData.storeName}. How can we help you today?`;
          window.open(`https://wa.me/${customer.phone}?text=${encodeURIComponent(message)}`);
        }
        break;
    }
  };

  const getCustomerSegment = (customer) => {
    const totalSpent = customer.totalSpent || 0;
    const orderCount = customer.totalOrders || 0;
    const daysSinceLastOrder = customer.lastOrderDate 
      ? Math.floor((new Date() - new Date(customer.lastOrderDate)) / (1000 * 60 * 60 * 24))
      : 999;

    if (totalSpent >= 5000 && orderCount >= 10) return { label: 'VIP', color: 'bg-purple-100 text-purple-800' };
    if (totalSpent >= 2000 && orderCount >= 5) return { label: 'Loyal', color: 'bg-blue-100 text-blue-800' };
    if (orderCount >= 3) return { label: 'Regular', color: 'bg-green-100 text-green-800' };
    if (orderCount === 1 && daysSinceLastOrder <= 30) return { label: 'New', color: 'bg-yellow-100 text-yellow-800' };
    if (daysSinceLastOrder > 90) return { label: 'At Risk', color: 'bg-red-100 text-red-800' };
    return { label: 'Potential', color: 'bg-gray-100 text-gray-800' };
  };

  const getLifetimeValue = (customer) => {
    return customer.totalSpent || 0;
  };

  const getCustomerStatus = (customer) => {
    const daysSinceLastOrder = customer.lastOrderDate 
      ? Math.floor((new Date() - new Date(customer.lastOrderDate)) / (1000 * 60 * 60 * 24))
      : 999;

    if (daysSinceLastOrder <= 30) return { label: 'Active', color: 'text-green-600' };
    if (daysSinceLastOrder <= 90) return { label: 'Inactive', color: 'text-yellow-600' };
    return { label: 'Churned', color: 'text-red-600' };
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.phone?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const segment = getCustomerSegment(customer);
    const matchesSegment = segmentFilter === 'all' || segment.label.toLowerCase() === segmentFilter;
    
    const status = getCustomerStatus(customer);
    const matchesStatus = statusFilter === 'all' || status.label.toLowerCase() === statusFilter;
    
    return matchesSearch && matchesSegment && matchesStatus;
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'email':
        comparison = (a.email || '').localeCompare(b.email || '');
        break;
      case 'totalSpent':
        comparison = (a.totalSpent || 0) - (b.totalSpent || 0);
        break;
      case 'totalOrders':
        comparison = (a.totalOrders || 0) - (b.totalOrders || 0);
        break;
      case 'lastOrder':
        comparison = new Date(a.lastOrderDate || 0) - new Date(b.lastOrderDate || 0);
        break;
      case 'joined':
        comparison = new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
              <p className="text-sm text-green-600">+{customerStats.newCustomersThisMonth || 0} this month</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {customers.filter(c => getCustomerStatus(c).label === 'Active').length}
              </p>
              <p className="text-sm text-blue-600">Last 30 days</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg. Lifetime Value</p>
              <p className="text-2xl font-bold text-gray-900">
                R{customers.length > 0 
                  ? (customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0) / customers.length).toFixed(2)
                  : '0.00'
                }
              </p>
              <p className="text-sm text-purple-600">Per customer</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">VIP Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {customers.filter(c => getCustomerSegment(c).label === 'VIP').length}
              </p>
              <p className="text-sm text-orange-600">High value</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Award className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Customers</h2>
          <p className="text-gray-600">
            {filteredCustomers.length} of {customers.length} customers
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => ecommerceService.exportCustomers(storeId)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>

          <button
            onClick={loadCustomers}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search customers by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={segmentFilter}
              onChange={(e) => setSegmentFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Segments</option>
              <option value="vip">VIP</option>
              <option value="loyal">Loyal</option>
              <option value="regular">Regular</option>
              <option value="new">New</option>
              <option value="at risk">At Risk</option>
              <option value="potential">Potential</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="churned">Churned</option>
            </select>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split('-');
                setSortBy(sort);
                setSortOrder(order);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="lastOrder-desc">Last Order (Recent)</option>
              <option value="lastOrder-asc">Last Order (Oldest)</option>
              <option value="totalSpent-desc">Highest Value</option>
              <option value="totalSpent-asc">Lowest Value</option>
              <option value="totalOrders-desc">Most Orders</option>
              <option value="name-asc">Name A-Z</option>
              <option value="joined-desc">Newest</option>
              <option value="joined-asc">Oldest</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : sortedCustomers.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Segment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedCustomers.map(customer => {
                const segment = getCustomerSegment(customer);
                const status = getCustomerStatus(customer);
                
                return (
                  <tr 
                    key={customer.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleCustomerClick(customer)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {customer.avatar ? (
                            <img
                              src={customer.avatar}
                              alt={customer.name}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <Users className="h-5 w-5 text-gray-500" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {customer.name || 'Anonymous'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {customer.email}
                          </div>
                          {customer.phone && (
                            <div className="text-sm text-gray-500">
                              {customer.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${segment.color}`}>
                        {segment.label}
                      </span>
                      <div className={`text-xs mt-1 ${status.color}`}>
                        {status.label}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {customer.totalOrders || 0} orders
                      </div>
                      <div className="text-sm text-gray-500">
                        Avg: R{customer.totalOrders > 0 
                          ? ((customer.totalSpent || 0) / customer.totalOrders).toFixed(2)
                          : '0.00'
                        }
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        R{(customer.totalSpent || 0).toFixed(2)}
                      </div>
                      {customer.totalOrders > 0 && (
                        <div className="text-xs text-gray-500">
                          Since {new Date(customer.createdAt?.toDate?.() || customer.createdAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    
                    <td className="px-6 py-4">
                      {customer.lastOrderDate ? (
                        <>
                          <div className="text-sm font-medium text-gray-900">
                            {new Date(customer.lastOrderDate).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {Math.floor((new Date() - new Date(customer.lastOrderDate)) / (1000 * 60 * 60 * 24))} days ago
                          </div>
                        </>
                      ) : (
                        <span className="text-sm text-gray-500">Never</span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {customer.email && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContactCustomer(customer, 'email');
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="Send Email"
                          >
                            <Mail className="h-4 w-4" />
                          </button>
                        )}
                        
                        {customer.phone && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContactCustomer(customer, 'phone');
                            }}
                            className="p-1 text-gray-400 hover:text-green-600"
                            title="Call"
                          >
                            <Phone className="h-4 w-4" />
                          </button>
                        )}

                        {customer.phone && storeData.integrations?.whatsapp && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContactCustomer(customer, 'whatsapp');
                            }}
                            className="p-1 text-gray-400 hover:text-green-600"
                            title="WhatsApp"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </button>
                        )}

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCustomerClick(customer);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
          <p className="text-gray-600">
            {searchQuery || segmentFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Customers will appear here when they place orders'
            }
          </p>
        </div>
      )}

      {/* Customer Detail Modal */}
      {showCustomerDetail && selectedCustomer && (
        <CustomerDetailModal
          customer={selectedCustomer}
          storeData={storeData}
          isOpen={showCustomerDetail}
          onClose={() => {
            setShowCustomerDetail(false);
            setSelectedCustomer(null);
          }}
          onContact={handleContactCustomer}
          onUpdate={loadCustomers}
        />
      )}
    </div>
  );
};